import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import type { CompletePlaceholderInput } from '../api/use-complete-placeholder-mutation';

const TYPES: readonly CompletePlaceholderInput['invoice_type'][] = [
  'Advance',
  'Provisional',
  'Final Settlement',
  'Adjustment',
];

export interface PlaceholderFormState {
  invoiceType: CompletePlaceholderInput['invoice_type'];
  invoiceNumber: string;
  issueDate: string;
  originalDueDate: string;
  remarks: string;
}

export const INITIAL_PLACEHOLDER_STATE: PlaceholderFormState = {
  invoiceType: 'Advance',
  invoiceNumber: '',
  issueDate: '',
  originalDueDate: '',
  remarks: '',
};

export function buildCompletePlaceholderBody(
  state: PlaceholderFormState,
): CompletePlaceholderInput {
  return {
    invoice_type: state.invoiceType,
    invoice_number: state.invoiceNumber.trim(),
    issue_date: state.issueDate,
    original_due_date: state.originalDueDate,
    ...(state.remarks.trim().length > 0 ? { remarks: state.remarks.trim() } : {}),
  };
}

export function canSubmitPlaceholder(state: PlaceholderFormState): boolean {
  return (
    state.invoiceNumber.trim().length > 0 &&
    state.issueDate.length > 0 &&
    state.originalDueDate.length > 0
  );
}

type Updater = <K extends keyof PlaceholderFormState>(
  key: K,
  value: PlaceholderFormState[K],
) => void;

export function PlaceholderForm({
  state,
  setState,
}: Readonly<{
  state: PlaceholderFormState;
  setState: (next: PlaceholderFormState) => void;
}>) {
  const update: Updater = (key, value) => {
    setState({ ...state, [key]: value });
  };
  return (
    <div className="space-y-3">
      <TypeField value={state.invoiceType} update={update} />
      <InvoiceNumberField value={state.invoiceNumber} update={update} />
      <DateFieldRow
        id="issue_date"
        label="Issue date"
        value={state.issueDate}
        onChange={(v) => {
          update('issueDate', v);
        }}
      />
      <DateFieldRow
        id="original_due_date"
        label="Original due date"
        value={state.originalDueDate}
        onChange={(v) => {
          update('originalDueDate', v);
        }}
      />
      <RemarksFieldRow value={state.remarks} update={update} />
    </div>
  );
}

function TypeField({
  value,
  update,
}: Readonly<{ value: CompletePlaceholderInput['invoice_type']; update: Updater }>) {
  return (
    <FieldRow id="invoice_type" label="Invoice type">
      <Select
        id="invoice_type"
        value={value}
        onChange={(e) => {
          update('invoiceType', e.target.value as CompletePlaceholderInput['invoice_type']);
        }}
      >
        {TYPES.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </Select>
    </FieldRow>
  );
}

function InvoiceNumberField({ value, update }: Readonly<{ value: string; update: Updater }>) {
  return (
    <FieldRow id="invoice_number" label="Invoice number">
      <Input
        id="invoice_number"
        value={value}
        placeholder="INV-001"
        onChange={(e) => {
          update('invoiceNumber', e.target.value);
        }}
      />
    </FieldRow>
  );
}

function DateFieldRow({
  id,
  label,
  value,
  onChange,
}: Readonly<{ id: string; label: string; value: string; onChange: (next: string) => void }>) {
  return (
    <FieldRow id={id} label={label}>
      <Input
        id={id}
        type="date"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
    </FieldRow>
  );
}

function RemarksFieldRow({ value, update }: Readonly<{ value: string; update: Updater }>) {
  return (
    <FieldRow id="remarks" label="Remarks (optional)">
      <Textarea
        id="remarks"
        value={value}
        rows={2}
        onChange={(e) => {
          update('remarks', e.target.value);
        }}
      />
    </FieldRow>
  );
}

function FieldRow({
  id,
  label,
  children,
}: Readonly<{ id: string; label: string; children: React.ReactNode }>) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}
